import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AdminUserAPI } from '../../services/userAdmin';
import { CustomerUseApi } from '../../services/userCustomer';
import { updateLoggedInUser } from './authSlice';

// Existing thunks
export const fetchAdmins = createAsyncThunk(
  'admins/fetchAdmins',
  async (_, { rejectWithValue }) => {
    try {
      const data = await AdminUserAPI.getListOfAdminUsers();
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const searchAdminUsers = createAsyncThunk(
  'admins/searchAdminUsers',
  async (formData, { rejectWithValue }) => {
    try {
      const data = await AdminUserAPI.searchAminUserAccounts(formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

// New thunks for admin management
export const registerAdmin = createAsyncThunk(
  'admins/registerAdmin',
  async (adminData, { rejectWithValue }) => {
    try {
      const data = await AdminUserAPI.registerAdminUser(adminData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const fetchAdminDetails = createAsyncThunk(
  'admins/fetchAdminDetails',
  async (adminId, { rejectWithValue }) => {
    try {
      const data = await AdminUserAPI.fetchAdminUser(adminId);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const updateAdmin = createAsyncThunk(
  'admins/updateAdmin',
  async (adminData, { rejectWithValue, getState, dispatch }) => {
    try {
      const { auth } = getState();
      const data = await AdminUserAPI.updateAdminUser(adminData);

      if (auth.user?.id == data.id) {
        localStorage.setItem('user', JSON.stringify(data)); // Update localStorage
        const updatedUser = { ...data, permissions: data.custom_permissions };
        dispatch(updateLoggedInUser(updatedUser)); // Update the logged-in user in the store

      }

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const deleteAdminAccounts = createAsyncThunk(
  'users/deleteCustomerAccounts',
  async (userIds, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.deleteCustomerAccounts(userIds);
      return { userIds, ...data }; // Return deleted IDs and any API response
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);


const adminsSlice = createSlice({
  name: 'admins',
  initialState: {
    loading: false,
    error: null,
    data: [],
    currentAdmin: null, // This is likely the issue
    singleAdmin: null,  // Add this if missing
    isSearchActive: false,
    operationSuccess: false
  },
  reducers: {
    clearSingleAdmin: (state) => {
      state.singleAdmin = null;
      state.error = null;
    },
    clearAdminError: (state) => {
      state.error = null;
    },

    resetAdminList: (state) => {
      state.isSearchActive = false;
    },
    resetCurrentAdmin: (state) => {
      state.currentAdmin = null;
    },
    resetOperationStatus: (state) => {
      state.operationSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Original fetchAdmins cases
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationSuccess = false;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.isSearchActive = false;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search cases
      .addCase(searchAdminUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.isSearchActive = true;
      })
      .addCase(searchAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Register admin
      .addCase(registerAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationSuccess = false;
      })
      .addCase(registerAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAdmin = action.payload;
        state.operationSuccess = true;
        state.data.unshift(action.payload); // Add new admin to the list
      })
      .addCase(registerAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch admin details
      .addCase(fetchAdminDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAdmin = action.payload;
      })
      .addCase(fetchAdminDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update admin
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationSuccess = false;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAdmin = action.payload;
        state.operationSuccess = true;
        // Update the admin in the data array
        state.data = state.data.map(admin =>
          admin.id === action.payload.id ? action.payload : admin
        );
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAdminAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminAccounts.fulfilled, (state, action) => {
        state.loading = false;
        // Remove deleted admins from state.data
        state.data = state.data.filter(admin => !action.payload.userIds.includes(admin.id));
      })
      .addCase(deleteAdminAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearSingleAdmin, resetAdminList, resetCurrentAdmin, resetOperationStatus } = adminsSlice.actions;
export default adminsSlice.reducer;